export type ApiClientErrorCode = "auth_required" | "http_error" | "network_error" | "invalid_response";

export class ApiClientError extends Error {
  readonly code: ApiClientErrorCode;
  readonly status?: number;
  readonly retryable: boolean;

  constructor(message: string, options: { code: ApiClientErrorCode; status?: number; retryable?: boolean }) {
    super(message);
    this.name = "ApiClientError";
    this.code = options.code;
    this.status = options.status;
    this.retryable = options.retryable ?? false;
  }
}

export type ApiClient = {
  get<TResponse>(path: string): Promise<TResponse>;
  post<TResponse, TBody>(path: string, body: TBody): Promise<TResponse>;
  patch<TResponse, TBody>(path: string, body: TBody): Promise<TResponse>;
};

type Fetcher = typeof fetch;

type ApiClientOptions = {
  baseUrl?: string;
  fetcher?: Fetcher;
  getAuthToken?: () => Promise<string | undefined> | string | undefined;
  getCSRFToken?: () => string | undefined;
};

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const fetcher = options.fetcher ?? fetch;

  return {
    async get<TResponse>(path: string): Promise<TResponse> {
      const response = await request(fetcher, buildUrl(options.baseUrl, path), {
        method: "GET",
        credentials: "same-origin",
        headers: await buildHeaders(options, false)
      });

      return readJson<TResponse>(response);
    },

    async post<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
      const headers = await buildHeaders(options, true);
      headers.set("Content-Type", "application/json");

      const response = await request(fetcher, buildUrl(options.baseUrl, path), {
        method: "POST",
        credentials: "same-origin",
        headers,
        body: JSON.stringify(body)
      });

      return readJson<TResponse>(response);
    },

    async patch<TResponse, TBody>(path: string, body: TBody): Promise<TResponse> {
      const headers = await buildHeaders(options, true);
      headers.set("Content-Type", "application/json");

      const response = await request(fetcher, buildUrl(options.baseUrl, path), {
        method: "PATCH",
        credentials: "same-origin",
        headers,
        body: JSON.stringify(body)
      });

      return readJson<TResponse>(response);
    }
  };
}

async function request(fetcher: Fetcher, input: RequestInfo | URL, init: RequestInit): Promise<Response> {
  try {
    const response = await fetcher(input, init);

    if (response.status === 401) {
      throw new ApiClientError("Authentication is required.", {
        code: "auth_required",
        status: response.status
      });
    }

    if (!response.ok) {
      throw new ApiClientError("The API request failed.", {
        code: "http_error",
        status: response.status,
        retryable: response.status >= 500
      });
    }

    return response;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw error;
    }

    throw new ApiClientError("The API request could not be completed.", {
      code: "network_error",
      retryable: true
    });
  }
}

async function readJson<TResponse>(response: Response): Promise<TResponse> {
  if (response.status === 204) {
    return undefined as TResponse;
  }

  try {
    return (await response.json()) as TResponse;
  } catch {
    throw new ApiClientError("The API response was not valid JSON.", {
      code: "invalid_response",
      status: response.status
    });
  }
}

async function buildHeaders(options: ApiClientOptions, includeCSRF: boolean): Promise<Headers> {
  const headers = new Headers({
    Accept: "application/json"
  });

  const token = await options.getAuthToken?.();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const csrfToken = includeCSRF ? options.getCSRFToken?.() ?? readCookie("thh_csrf_token") : undefined;
  if (csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }

  return headers;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  if (!cookie) {
    return undefined;
  }

  try {
    return decodeURIComponent(cookie.slice(prefix.length));
  } catch {
    return undefined;
  }
}

function buildUrl(baseUrl: string | undefined, path: string): string {
  if (!baseUrl) {
    return path;
  }

  return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}
